import React, { useState, useEffect } from 'react';
import { CLUB_NAMES, ACTIVITY_TYPES } from '../config';
import './Rewards.css';

function Rewards({ userId, userData, userActivities, contract, claimRewards, addNotification, refreshData }) {
  const [clubInfo, setClubInfo] = useState({ level: 0, claimed: [] });
  const [clubRequirements, setClubRequirements] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (contract && userId > 0) fetchClubData();
  }, [contract, userId]);

  const fetchClubData = async () => {
    if (!contract || userId <= 0) return;
    try {
      const info = await contract.getUserClubInfo(userId);
      setClubInfo({ level: info[0].toNumber(), claimed: info[1] });

      const requirements = [];
      for (let i = 0; i < 8; i++) {
        const req = await contract.clubRequirements(i);
        requirements.push({
          teamBusiness: parseFloat(req.teamBusiness.toString()) / 1e18,
          directBusiness: parseFloat(req.directBusiness.toString()) / 1e18,
          selfStaking: parseFloat(req.selfStaking.toString()) / 1e18,
          reward: parseFloat(req.reward.toString()) / 1e18
        });
      }
      setClubRequirements(requirements);
    } catch (error) {
      console.error('Failed to fetch club data:', error);
    }
  };

  // Format number with commas and proper decimals
  const formatNumber = (num) => {
    if (!num || num === 0) return '0';
    const n = parseFloat(num);
    if (n >= 1000000) return (n / 1000000).toFixed(2) + 'M';
    if (n >= 100000) return (n / 1000).toFixed(0) + 'K';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toFixed(0);
  };

  // Format for display with $ sign
  const formatCurrency = (num) => {
    return '$' + formatNumber(num);
  };

  // Get direct referral bonus tier
  const getDirectBonusTier = () => {
    const directBiz = parseFloat(userData?.directBusiness || 0);
    if (directBiz >= 5010) return { percent: 20, tier: 'Tier 3', min: 5010, max: '∞', current: directBiz };
    if (directBiz >= 1010) return { percent: 15, tier: 'Tier 2', min: 1010, max: 5000, current: directBiz };
    if (directBiz >= 100) return { percent: 10, tier: 'Tier 1', min: 100, max: 1000, current: directBiz };
    return { percent: 0, tier: 'None', min: 100, max: 1000, current: directBiz };
  };

  const handleWithdraw = async () => {
    if (!userData || parseFloat(userData.pendingRewards) <= 0) {
      addNotification('error', 'No rewards to withdraw');
      return;
    }
    setIsLoading(true);
    try {
      await claimRewards();
      addNotification('success', 'Withdrawal successful!');
      await refreshData();
    } catch (error) {
      addNotification('error', error.message || 'Withdrawal failed');
    } finally {
      setIsLoading(false);
    }
  };

  const getClubProgress = (index) => {
    if (!userData || !clubRequirements[index]) return 0;
    const req = clubRequirements[index];
    const t = Math.min((parseFloat(userData.teamVolume) / req.teamBusiness) * 100, 100);
    const d = Math.min((parseFloat(userData.directBusiness) / req.directBusiness) * 100, 100);
    const s = Math.min((parseFloat(userData.totalInvestment) / req.selfStaking) * 100, 100);
    return (t + d + s) / 3;
  };

  const formatTime = (ts) => new Date(ts * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  const pendingRewards = parseFloat(userData?.pendingRewards || 0);
  const totalEarned = parseFloat(userData?.totalEarned || 0);
  const bonusTier = getDirectBonusTier();

  return (
    <div className="rewards-page">
      <div className="rewards-header">
        <img src="/5.gif" alt="Rewards" className="header-gif" />
        <h1 className="page-title">Rewards Center</h1>
      </div>

      {/* Pending Rewards */}
      <div className="pending-card animate-fade-in-up">
        <div className="pending-content">
          <span className="pending-label">Available to Withdraw</span>
          <div className="pending-amount">${pendingRewards.toFixed(2)}</div>
        </div>
        <button className="withdraw-btn" onClick={handleWithdraw} disabled={isLoading || pendingRewards <= 0}>
          {isLoading ? <span className="btn-spinner" /> : 'Withdraw'}
        </button>
        <div className="fee-notice">10% withdrawal fee applies</div>
      </div>

      {/* Earnings Summary */}
      <div className="earnings-grid">
        <div className="earning-card">
          <div className="ec-icon">💰</div>
          <div className="ec-value">${totalEarned.toFixed(2)}</div>
          <div className="ec-label">Total Earned</div>
        </div>
        <div className="earning-card">
          <div className="ec-icon">🏆</div>
          <div className="ec-value">{CLUB_NAMES[clubInfo.level - 1] || 'None'}</div>
          <div className="ec-label">Club Level</div>
        </div>
      </div>

      {/* Direct Referral Bonus Eligibility */}
      <div className="bonus-section">
        <h2 className="section-title">Direct Referral Bonus</h2>
        <div className="bonus-card">
          <div className="bonus-header">
            <div className="bonus-icon">🎁</div>
            <div className="bonus-info">
              <span className="bonus-tier">{bonusTier.tier}</span>
              <span className="bonus-percent">{bonusTier.percent}% Bonus</span>
            </div>
            <div className="bonus-status">
              {bonusTier.percent > 0 ? '✅ Active' : '⏳ Inactive'}
            </div>
          </div>
          
          <div className="bonus-progress">
            <div className="bonus-current">
              Current: <strong>{formatCurrency(bonusTier.current)}</strong>
            </div>
          </div>

          <div className="bonus-tiers">
            <div className={`tier-item ${bonusTier.percent >= 10 ? 'achieved' : ''}`}>
              <span className="tier-name">Tier 1 (10%)</span>
              <span className="tier-req">${formatNumber(100)} - ${formatNumber(1000)}</span>
            </div>
            <div className={`tier-item ${bonusTier.percent >= 15 ? 'achieved' : ''}`}>
              <span className="tier-name">Tier 2 (15%)</span>
              <span className="tier-req">${formatNumber(1010)} - ${formatNumber(5000)}</span>
            </div>
            <div className={`tier-item ${bonusTier.percent >= 20 ? 'achieved' : ''}`}>
              <span className="tier-name">Tier 3 (20%)</span>
              <span className="tier-req">${formatNumber(5010)}+</span>
            </div>
          </div>
        </div>
      </div>

      {/* Club Rewards */}
      <div className="club-section">
        <h2 className="section-title">Club Rewards</h2>
        <div className="clubs-list">
          {CLUB_NAMES.map((name, index) => {
            const req = clubRequirements[index];
            const progress = getClubProgress(index);
            const isAchieved = clubInfo.level > index;
            const isClaimed = clubInfo.claimed[index];

            return (
              <div key={index} className={`club-card ${isAchieved ? 'achieved' : ''}`}>
                <div className="club-header">
                  <div className="club-badge">{index + 1}</div>
                  <div className="club-info">
                    <span className="club-name">{name}</span>
                    <span className="club-status">
                      {isAchieved ? (isClaimed ? '✓ Claimed' : '🎉 Achieved') : `${progress.toFixed(0)}%`}
                    </span>
                  </div>
                  <div className="club-reward">${formatNumber(req?.reward || 0)}</div>
                </div>

                {!isAchieved && req && (
                  <div className="club-reqs">
                    <div className="req-bar">
                      <span className="req-label">
                        Team: {formatCurrency(parseFloat(userData?.teamVolume || 0))} / {formatCurrency(req.teamBusiness)}
                      </span>
                      <div className="req-progress">
                        <div 
                          className="req-fill" 
                          style={{ width: `${Math.min((parseFloat(userData?.teamVolume || 0) / req.teamBusiness) * 100, 100)}%` }} 
                        />
                      </div>
                    </div>
                    <div className="req-bar">
                      <span className="req-label">
                        Direct: {formatCurrency(parseFloat(userData?.directBusiness || 0))} / {formatCurrency(req.directBusiness)}
                      </span>
                      <div className="req-progress">
                        <div 
                          className="req-fill purple" 
                          style={{ width: `${Math.min((parseFloat(userData?.directBusiness || 0) / req.directBusiness) * 100, 100)}%` }} 
                        />
                      </div>
                    </div>
                    <div className="req-bar">
                      <span className="req-label">
                        Self: {formatCurrency(parseFloat(userData?.totalInvestment || 0))} / {formatCurrency(req.selfStaking)}
                      </span>
                      <div className="req-progress">
                        <div 
                          className="req-fill green" 
                          style={{ width: `${Math.min((parseFloat(userData?.totalInvestment || 0) / req.selfStaking) * 100, 100)}%` }} 
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="activity-section">
        <h2 className="section-title">Recent Activity</h2>
        {userActivities.length === 0 ? (
          <div className="empty-activity">No activity yet</div>
        ) : (
          <div className="activity-list">
            {userActivities.slice(0, 10).map((act, i) => {
              const type = ACTIVITY_TYPES[act.activityType] || { label: 'Unknown', icon: '❓', color: '#888' };
              return (
                <div key={i} className="activity-item" style={{ '--accent': type.color }}>
                  <div className="act-icon">{type.icon}</div>
                  <div className="act-info">
                    <span className="act-type">{type.label}</span>
                    <span className="act-time">{formatTime(act.timestamp)}</span>
                  </div>
                  <span className="act-amount" style={{ color: type.color }}>+${parseFloat(act.amount).toFixed(2)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Rewards;
